import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import { 
  LuCloud, LuPlus, LuTrash2, LuCheck, LuDownload, LuRefreshCw, 
  LuDollarSign, LuSearch, LuServer, LuActivity, LuShield,
  LuX, LuCircleAlert, LuChevronRight, LuZap, LuGlobe,
  LuDatabase, LuHardDrive, LuNetwork
} from 'react-icons/lu';
import { VscAzure } from 'react-icons/vsc';
import { FaAws } from 'react-icons/fa';
import { SiLinux, SiOracle, SiContabo, SiGooglecloud } from 'react-icons/si';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import AttachServerDialog from '../components/AttachServerDialog';
import { getProviderReadiness, getProviderSummary } from '../utils/operationalReadiness';

// Capability configuration
const CAPABILITY_CONFIG = {
  validate: { icon: LuShield, label: 'Auth' },
  discover: { icon: LuSearch, label: 'Discover' },
  list: { icon: LuServer, label: 'List' },
  create: { icon: LuPlus, label: 'Create' },
  delete: { icon: LuTrash2, label: 'Delete' },
  start_stop: { icon: LuActivity, label: 'Control' },
  metrics: { icon: LuActivity, label: 'Metrics' },
  cost_explorer: { icon: LuDollarSign, label: 'Costs' },
  sync: { icon: LuRefreshCw, label: 'Sync' },
  attach_existing: { icon: LuPlus, label: 'Attach' },
};

// Provider type icons
const PROVIDER_ICONS = {
  aws: { icon: FaAws, color: '#f59e0b', label: 'Amazon Web Services' },
  azure: { icon: VscAzure, color: '#3b82f6', label: 'Microsoft Azure' },
  vanilla: { icon: SiLinux, color: '#22c55e', label: 'Custom Server' },
  oracle: { icon: SiOracle, color: '#ef4444', label: 'Oracle Cloud' },
  contabo: { icon: SiContabo, color: '#3b82f6', label: 'Contabo' },
  gcp: { icon: SiGooglecloud, color: '#4285F4', label: 'Google Cloud Platform' }
};

const RESOURCE_ICONS = {
  vm: LuServer,
  database: LuDatabase,
  storage: LuHardDrive,
  network: LuNetwork,
  load_balancer: LuGlobe,
};

const STATUS_STYLES = {
  running: 'bg-teal-500/10 text-teal-400',
  stopped: 'bg-rose-500/10 text-rose-400',
  transitioning: 'bg-blue-500/10 text-blue-400',
  degraded: 'bg-amber-500/10 text-amber-400',
  unknown: 'bg-zinc-700/50 text-zinc-400',
};

const messageFor = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return (typeof detail === 'string' ? detail : detail?.message) || fallback;
};

// Header Component
const ProvidersHeader = ({ providers, onAddClick, addDisabled = false }) => {
  const statement = useMemo(() => getProviderSummary(providers), [providers]);

  return (
    <div className="mb-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-100 mb-2">{statement.primary}</h1>
          <p className="text-base text-zinc-500">{statement.secondary}</p>
          {providers.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="rounded-md bg-zinc-800 px-2.5 py-1 text-zinc-300">
                {statement.inventoryCapable} inventory-capable
              </span>
              <span className={`rounded-md px-2.5 py-1 ${statement.provisioningEnabled ? 'bg-teal-500/10 text-teal-400' : 'bg-amber-500/10 text-amber-400'}`}>
                {statement.provisioningEnabled} provisioning enabled
              </span>
            </div>
          )}
        </div>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onAddClick}
          disabled={addDisabled}
          title={addDisabled ? 'Provider setup options are unavailable' : undefined}
          className="px-4 py-2 text-sm font-medium bg-amber-500 hover:bg-amber-400 text-zinc-900 rounded-lg transition-all flex items-center gap-2 flex-shrink-0 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-400"
        >
          <LuPlus className="w-4 h-4" />
          Add Provider
        </motion.button>
      </div>
    </div>
  );
};

// Provider Row Component
const ProviderRow = ({ provider, resources, resourcesLoading, resourcesError, onDiscover, onAttach, onDelete, onCheckCapabilities }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  
  const providerConfig = PROVIDER_ICONS[provider.type] || { icon: LuCloud, color: '#71717a', label: 'Cloud Provider' };
  const Icon = providerConfig.icon;
  
  // Convert capabilities dict to array of keys where value is true
  const capabilitiesDict = provider.capabilities || {};
  const capabilities = Object.entries(capabilitiesDict)
    .filter(([_, isAvailable]) => isAvailable)
    .map(([key, _]) => key);
  const isConnected = provider.status === 'connected';
  const resourceCount = provider.resource_count || 0;
  const readiness = getProviderReadiness(provider);

  const handleDiscover = async () => {
    setIsDiscovering(true);
    await onDiscover(provider.id, provider.name);
    setIsDiscovering(false);
  };

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      {/* Main Row */}
      <div className="flex items-center gap-4 px-5 py-4">
        {/* Status indicator */}
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
          isConnected ? 'bg-teal-400' : 'bg-amber-400'
        }`} />
        
        {/* Provider icon */}
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${providerConfig.color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color: providerConfig.color }} />
        </div>
        
        {/* Provider info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-zinc-200">{provider.name}</span>
            <span className={`px-2 py-0.5 rounded text-xs ${
              isConnected 
                ? 'bg-teal-500/10 text-teal-400' 
                : 'bg-amber-500/10 text-amber-400'
            }`}>
              {isConnected ? 'Authenticated' : 'Not authenticated'}
            </span>
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">
            {providerConfig.label}
            <span className="sm:hidden"> · {resourceCount} {resourceCount === 1 ? 'resource' : 'resources'}</span>
          </p>
        </div>

        <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-zinc-800/70 text-xs text-zinc-300">
          <LuServer className="w-3.5 h-3.5 text-zinc-500" />
          {resourceCount} {resourceCount === 1 ? 'resource' : 'resources'}
        </div>
        
        {/* The expanded panel carries the complete capability matrix. Keep the
            row summary readable without relying on hover-only icon tooltips. */}
        <div
          className="hidden items-center rounded-lg bg-zinc-800/70 px-2.5 py-1 text-xs text-zinc-400 lg:flex"
          aria-label={capabilities.length
            ? `Adapter exposes ${capabilities.length} capabilities: ${capabilities.map((cap) => CAPABILITY_CONFIG[cap]?.label || cap).join(', ')}`
            : 'Adapter capabilities are unavailable'}
        >
          {capabilities.length ? `${capabilities.length} capabilities` : 'Capabilities unavailable'}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {provider.existing_resources?.enabled && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onAttach(provider)}
              className="min-h-11 px-3 py-2 text-xs font-medium bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 rounded-lg transition-all flex items-center gap-1.5"
            >
              <LuPlus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{provider.existing_resources.label || 'Add server'}</span>
              <span className="sm:hidden">Add</span>
            </motion.button>
          )}
          {capabilities.includes('discover') && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDiscover}
              aria-label={`Discover resources from ${provider.name}`}
              disabled={isDiscovering}
              className="min-h-11 px-3 py-2 text-xs font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 rounded-lg transition-all flex items-center gap-1.5 disabled:opacity-50"
            >
              {isDiscovering ? (
                <LuRefreshCw className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <LuDownload className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">Discover</span>
            </motion.button>
          )}
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${provider.name}`}
            aria-expanded={isExpanded}
            className="flex min-h-11 min-w-11 items-center justify-center p-2 text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <LuChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Expanded Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-zinc-800"
          >
            <div className="px-5 py-4 bg-zinc-900/50">
              <div className="mb-6">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Servers &amp; resources</p>
                  <span className="text-xs text-zinc-500">
                    {resourceCount} {resourceCount === 1 ? 'linked resource' : 'linked resources'}
                  </span>
                </div>

                {resourcesLoading ? (
                  <div className="flex items-center gap-2 py-3 text-xs text-zinc-500" role="status">
                    <LuRefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Loading linked resources…
                  </div>
                ) : resourcesError ? (
                  <div className="flex items-center gap-2 py-3 text-xs text-amber-400" role="alert">
                    <LuCircleAlert className="w-3.5 h-3.5" />
                    Resource details could not be loaded. The provider connection is unchanged.
                  </div>
                ) : resources.length > 0 ? (
                  <div className="space-y-2">
                    {resources.map(resource => {
                      const ResourceIcon = RESOURCE_ICONS[resource.type] || LuServer;
                      const statusStyle = STATUS_STYLES[resource.status] || STATUS_STYLES.unknown;
                      return (
                        <Link
                          key={resource.id}
                          to={`/resources/${resource.id}`}
                          className="group flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950/40 px-3 py-2.5 hover:border-zinc-700 hover:bg-zinc-800/60 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-md bg-zinc-800 flex items-center justify-center flex-shrink-0">
                            <ResourceIcon className="w-4 h-4 text-zinc-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-zinc-200 truncate group-hover:text-amber-300 transition-colors">
                              {resource.name}
                            </p>
                            <p className="text-xs text-zinc-500 truncate">
                              {resource.type === 'vm' ? 'Server' : (resource.type || 'resource').replace('_', ' ')}
                              {resource.region ? ` · ${resource.region}` : ''}
                            </p>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs capitalize ${statusStyle}`}>
                            {resource.status || 'unknown'}
                          </span>
                          <LuChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400" />
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-zinc-800 px-4 py-4 text-center">
                    <p className="text-sm text-zinc-400">No resources linked to this provider</p>
                    <p className="text-xs text-zinc-600 mt-1">
                      {provider.existing_resources?.enabled
                        ? 'Add an existing server to begin building this inventory.'
                        : 'Run Discover to import resources already present in the provider account.'}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Capabilities */}
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Operational readiness</p>
                  <div className="mb-4 space-y-2 text-xs">
                    <p className={readiness.authenticated ? 'text-teal-400' : 'text-amber-400'}>{readiness.authenticationLabel}</p>
                    <p className={readiness.inventoryAvailable ? 'text-teal-400' : 'text-zinc-500'}>{readiness.inventoryLabel}</p>
                    <p className={readiness.provisioningEnabled ? 'text-teal-400' : 'text-amber-400'}>{readiness.provisioningLabel}</p>
                    <p className="text-zinc-500">{readiness.controlsLabel}</p>
                  </div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Adapter capabilities</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(CAPABILITY_CONFIG).map(([key, config]) => {
                      const isAvailable = capabilities.includes(key);
                      const CapIcon = config.icon;
                      return (
                        <div 
                          key={key}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
                            isAvailable 
                              ? 'bg-teal-500/10 text-teal-400 border border-teal-500/20' 
                              : 'bg-zinc-800/50 text-zinc-600 border border-zinc-800'
                          }`}
                        >
                          <CapIcon className="w-3 h-3" />
                          {config.label}
                          {isAvailable ? (
                            <LuCheck className="w-3 h-3" />
                          ) : (
                            <LuX className="w-3 h-3" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Actions */}
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Actions</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => onCheckCapabilities(provider.id, provider.name)}
                      className="px-3 py-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-200 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-all flex items-center gap-1.5"
                    >
                      <LuRefreshCw className="w-3.5 h-3.5" />
                      Refresh Capabilities
                    </button>
                    <button
                      onClick={() => onDelete(provider.id)}
                      className="px-3 py-1.5 text-xs font-medium text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all flex items-center gap-1.5"
                    >
                      <LuTrash2 className="w-3.5 h-3.5" />
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Empty State
const EmptyState = ({ onAddClick }) => (
  <div className="text-center py-16">
    <div className="w-16 h-16 bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
      <LuCloud className="w-8 h-8 text-zinc-600" />
    </div>
    <h3 className="text-lg font-medium text-zinc-300 mb-2">No providers connected</h3>
    <p className="text-sm text-zinc-500 mb-6 max-w-md mx-auto">
      Connect your cloud providers to discover and manage your infrastructure from one place.
    </p>
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onAddClick}
      className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-zinc-900 rounded-lg font-medium transition-colors inline-flex items-center gap-2"
    >
      <LuPlus className="w-4 h-4" />
      Add Your First Provider
    </motion.button>
  </div>
);

// Add Provider Dialog
const AddProviderDialog = ({ 
  open, 
  onClose, 
  providerTypes, 
  formData, 
  setFormData, 
  onSubmit, 
  onValidate, 
  validating, 
  validationResult 
}) => {
  const selectedType = providerTypes.find(t => t.type === formData.type);
  const credentials = selectedType?.required_credentials || [];
  const providerConfig = PROVIDER_ICONS[formData.type] || { icon: LuCloud, color: '#71717a' };
  const Icon = providerConfig.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-zinc-100">Add Provider</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={onSubmit} className="space-y-5 mt-4">
          {/* Provider Type Selection */}
          <div>
            <span id="provider-type-label" className="text-xs text-zinc-500 uppercase tracking-wider">Provider Type</span>
            <div className="grid grid-cols-2 gap-2 mt-2 sm:grid-cols-3" role="radiogroup" aria-labelledby="provider-type-label">
              {providerTypes.map(type => {
                const config = PROVIDER_ICONS[type.type] || { icon: LuCloud, color: '#71717a' };
                const TypeIcon = config.icon;
                const isSelected = formData.type === type.type;
                return (
                  <button
                    key={type.type}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setFormData({ ...formData, type: type.type, credentials: {} })}
                    className={`p-3 rounded-lg border transition-all ${
                      isSelected 
                        ? 'border-amber-500 bg-amber-500/10' 
                        : 'border-zinc-800 hover:border-zinc-700 bg-zinc-800/50'
                    }`}
                  >
                    <TypeIcon 
                      className="w-6 h-6 mx-auto mb-1" 
                      style={{ color: isSelected ? config.color : '#71717a' }} 
                    />
                    <p className={`text-xs ${isSelected ? 'text-zinc-200' : 'text-zinc-500'}`}>
                      {type.name?.split(' ')[0] || type.type}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Provider Name */}
          <div>
            <Label htmlFor="provider-display-name" className="text-xs text-zinc-500 uppercase tracking-wider">Display Name</Label>
            <Input
              id="provider-display-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="My AWS Account"
              className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100"
              required
            />
          </div>
          
          {/* Dynamic Credentials */}
          {credentials.map(cred => (
            <div key={cred.name}>
              <Label htmlFor={`provider-credential-${cred.name}`} className="text-xs text-zinc-500 uppercase tracking-wider">
                {cred.label}
                {cred.required && <span className="text-rose-400 ml-1">*</span>}
              </Label>
              {cred.type === 'textarea' ? <textarea
                id={`provider-credential-${cred.name}`}
                rows={6}
                spellCheck="false"
                autoComplete="off"
                value={formData.credentials[cred.name] || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  credentials: { ...formData.credentials, [cred.name]: e.target.value }
                })}
                placeholder={cred.placeholder || ''}
                className="mt-2 min-h-32 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 font-mono text-sm text-zinc-100"
                required={cred.required}
              /> : <Input
                id={`provider-credential-${cred.name}`}
                type={cred.type === 'password' ? 'password' : 'text'}
                autoComplete={cred.type === 'password' ? 'new-password' : 'off'}
                value={formData.credentials[cred.name] || ''}
                onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, [cred.name]: e.target.value } })}
                placeholder={cred.placeholder || ''}
                className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100"
                required={cred.required}
              />}
              {cred.help && (
                <p className="text-xs text-zinc-600 mt-1">{cred.help}</p>
              )}
            </div>
          ))}
          
          {/* Validation Result */}
          {validationResult && (
            <div className={`p-3 rounded-lg ${
              validationResult.valid 
                ? 'bg-teal-500/10 border border-teal-500/20' 
                : 'bg-rose-500/10 border border-rose-500/20'
            }`}>
              <div className="flex items-center gap-2">
                {validationResult.valid ? (
                  <LuCheck className="w-4 h-4 text-teal-400" />
                ) : (
                  <LuCircleAlert className="w-4 h-4 text-rose-400" />
                )}
                <span className={`text-sm ${validationResult.valid ? 'text-teal-400' : 'text-rose-400'}`}>
                  {validationResult.valid ? 'Credentials valid' : validationResult.error || 'Validation failed'}
                </span>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onValidate}
              disabled={validating}
              className="px-4 py-2 text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
            >
              {validating ? (
                <LuRefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <LuShield className="w-4 h-4" />
              )}
              Validate
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 text-sm font-medium bg-amber-500 hover:bg-amber-400 text-zinc-900 rounded-lg transition-all"
            >
              Add Provider
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const Providers = ({ user, onLogout }) => {
  const [providers, setProviders] = useState([]);
  const [resources, setResources] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providersError, setProvidersError] = useState(null);
  const [typesError, setTypesError] = useState(null);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [resourcesError, setResourcesError] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [attachProvider, setAttachProvider] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'aws',
    credentials: {},
  });

  useEffect(() => {
    loadProviders();
    loadResources();
    loadProviderTypes();
  }, []);

  const loadProviders = async () => {
    setProvidersError(null);
    try {
      const response = await api.getProviders();
      setProviders(response.data);
    } catch (error) {
      setProvidersError(messageFor(error, 'Providers could not be loaded.'));
    } finally {
      setLoading(false);
    }
  };

  const loadResources = async () => {
    setResourcesLoading(true);
    setResourcesError(false);
    try {
      const response = await api.getResources();
      setResources(response.data);
    } catch (error) {
      setResourcesError(true);
    } finally {
      setResourcesLoading(false);
    }
  };

  const loadProviderTypes = async () => {
    setTypesError(null);
    try {
      const response = await api.get('/providers/types');
      setProviderTypes(response.data.providers || []);
      if (response.data.providers?.length > 0) {
        setFormData(prev => ({ ...prev, type: response.data.providers[0].type }));
      }
    } catch (error) {
      setProviderTypes([]);
      setTypesError(messageFor(error, 'Provider setup options could not be verified.'));
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    setValidationResult(null);
    
    try {
      const response = await api.post('/providers/validate', formData);
      setValidationResult(response.data);
      if (response.data.valid) {
        toast.success('Credentials validated!');
      }
    } catch (error) {
      setValidationResult({ valid: false, error: messageFor(error, 'Validation failed') });
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createProvider(formData);
      toast.success('Provider added');
      setShowDialog(false);
      setFormData({ name: '', type: providerTypes[0]?.type || 'aws', credentials: {} });
      setValidationResult(null);
      loadProviders();
    } catch (error) {
      toast.error(messageFor(error, 'Failed to add provider'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this provider?')) return;
    try {
      await api.deleteProvider(id);
      toast.success('Provider removed');
      loadProviders();
    } catch (error) {
      toast.error(messageFor(error, 'Failed to remove provider'));
    }
  };

  const handleDiscover = async (providerId, providerName) => {
    const toastId = toast.loading(`Discovering resources from ${providerName}...`);
    try {
      const response = await api.discoverProviderResources(providerId);
      toast.success(response.data.message, { id: toastId });
      await Promise.all([loadProviders(), loadResources()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Discovery failed', { id: toastId });
    }
  };

  const handleAttach = async (provider, data) => {
    const response = await api.attachExistingResource(provider.id, data);
    toast.success(`${response.data.name} added to inventory. Configure secure access from its Terminal tab.`);
    await Promise.all([loadProviders(), loadResources()]);
    return response;
  };

  const handleCheckCapabilities = async (providerId, providerName) => {
    const toastId = toast.loading(`Checking ${providerName}...`);
    try {
      await api.post(`/providers/${providerId}/check-capabilities`);
      toast.success('Capabilities updated', { id: toastId });
      loadProviders();
    } catch (error) {
      toast.error('Failed to check capabilities', { id: toastId });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-zinc-700 border-t-amber-400 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-500 text-sm">Loading providers...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8">
        {providersError && <div role="alert" className="mb-6 rounded-xl border border-red-500/25 bg-red-500/10 p-5 text-red-100"><p className="font-medium">Providers are unavailable</p><p className="mt-1 text-sm text-red-200/80">{providersError}</p><button onClick={loadProviders} className="mt-3 min-h-10 rounded-lg border border-red-400/30 px-3 text-sm font-medium">Retry</button></div>}
        {typesError && <div role="alert" className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/10 p-5 text-amber-100"><p className="font-medium">Adding providers is temporarily unavailable</p><p className="mt-1 text-sm text-amber-200/80">{typesError} Existing connections are unchanged.</p><button onClick={loadProviderTypes} className="mt-3 min-h-10 rounded-lg border border-amber-400/30 px-3 text-sm font-medium">Retry setup options</button></div>}
        <ProvidersHeader 
          providers={providers} 
          onAddClick={() => { if (!typesError && providerTypes.length) setShowDialog(true); }}
          addDisabled={Boolean(typesError) || !providerTypes.length}
        />

        {!providersError && providers.length === 0 ? (
          <EmptyState onAddClick={() => { if (!typesError && providerTypes.length) setShowDialog(true); }} />
        ) : !providersError ? (
          <div className="space-y-3">
            {providers.map(provider => (
              <ProviderRow
                key={provider.id}
                provider={provider}
                resources={resources.filter(resource => resource.provider_id === provider.id)}
                resourcesLoading={resourcesLoading}
                resourcesError={resourcesError}
                onDiscover={handleDiscover}
                onAttach={setAttachProvider}
                onDelete={handleDelete}
                onCheckCapabilities={handleCheckCapabilities}
              />
            ))}
          </div>
        ) : null}

        <AddProviderDialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          providerTypes={providerTypes}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onValidate={handleValidate}
          validating={validating}
          validationResult={validationResult}
        />
        <AttachServerDialog
          open={Boolean(attachProvider)}
          provider={attachProvider}
          onClose={() => setAttachProvider(null)}
          onSubmit={handleAttach}
        />
      </div>
    </div>
  );
};

export default Providers;
